import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useSession } from "./state/session.js";
import { useContacts, useUnreadCount } from "./hooks.js";
import { setBadgeCount } from "./badge.js";
import { playMessageTone } from "./calling/tones.js";
import { showLocalNotification } from "./notifications.js";
import { messaging } from "./services/messaging.js";
import { useCallStore } from "./state/call.js";
import { Onboarding } from "./features/Onboarding.js";
import { InviteLanding } from "./features/InviteLanding.js";
import { Unlock } from "./features/Unlock.js";
import { Home } from "./features/Home.js";
import { AddContact } from "./features/AddContact.js";
import { Chat } from "./features/Chat.js";
import { Settings } from "./features/Settings.js";
import { AddByInvite } from "./features/AddByInvite.js";
import { CallOverlay } from "./features/CallOverlay.js";

/** Keeps the home-screen icon's number badge in sync with unread messages.
 *  Mounted only while a session is unlocked — see the "ready" branch below. */
function UnreadBadge() {
  const unread = useUnreadCount();
  useEffect(() => {
    void setBadgeCount(unread);
  }, [unread]);
  return null;
}

/** Keeps the relay following every contact's online status as contacts come and go. */
function PresenceWatch() {
  const ids = useContacts()
    .map((c) => c.peerUserId)
    .sort()
    .join(",");
  useEffect(() => {
    messaging.watchPresence(ids ? ids.split(",") : []);
  }, [ids]);
  return null;
}

/** Chime for each new message (not mid-call), plus a notification if the app is open but out of sight. */
function MessageAlerts() {
  useEffect(
    () =>
      messaging.onIncomingMessage(() => {
        if (useCallStore.getState().phase === "idle") playMessageTone();
        void showLocalNotification("message");
      }),
    [],
  );
  return null;
}

export function App() {
  const phase = useSession((s) => s.phase);

  if (phase === "loading") {
    return (
      <div className="app">
        <div className="center">
          <p>Starting…</p>
        </div>
      </div>
    );
  }

  if (phase === "onboarding") {
    return (
      <div className="app">
        <Routes>
          <Route path="/add/:token" element={<InviteLanding />} />
          <Route path="*" element={<Onboarding />} />
        </Routes>
      </div>
    );
  }

  if (phase === "locked") {
    return (
      <div className="app">
        <Unlock />
      </div>
    );
  }

  return (
    <div className="app">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/add" element={<AddContact />} />
        <Route path="/add/:token" element={<AddByInvite />} />
        <Route path="/chat/:peerUserId" element={<Chat />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CallOverlay />
      <UnreadBadge />
      <MessageAlerts />
      <PresenceWatch />
    </div>
  );
}
